import React from 'react';

type UseThumbnailOptions = {
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: string;
  quality?: number;
};

type ThumbnailState = 'idle' | 'loading' | 'ready' | 'error';

const DEFAULT_OPTIONS: Required<UseThumbnailOptions> = {
  maxWidth: 512,
  maxHeight: 512,
  mimeType: 'image/jpeg',
  quality: 0.75,
};

const thumbnailBlobCache = new WeakMap<File, Promise<Blob>>();

/**
 * Generates (and caches) a downscaled thumbnail Blob for a File.
 * Returns an object URL that is revoked when the component unmounts.
 */
export default function useThumbnailUrl(
  file?: File,
  options: UseThumbnailOptions = {}
): { url?: string; status: ThumbnailState } {
  const [{ url, status }, setState] = React.useState<{
    url?: string;
    status: ThumbnailState;
  }>({ status: 'idle' });

  const { maxWidth, maxHeight, mimeType, quality } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  React.useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setState((prev) => {
        if (prev.url) {
          URL.revokeObjectURL(prev.url);
        }
        return { status: 'idle' };
      });
      return;
    }

    let cancelled = false;
    let nextUrl: string | undefined;

    setState((prev) => {
      if (prev.url) {
        URL.revokeObjectURL(prev.url);
      }
      return { status: 'loading' };
    });

    getThumbnailBlob(file, {
      maxWidth,
      maxHeight,
      mimeType,
      quality,
    })
      .then((blob) => {
        if (cancelled) {
          return;
        }
        nextUrl = URL.createObjectURL(blob);
        setState({ url: nextUrl, status: 'ready' });
      })
      .catch((error) => {
        console.error('Failed to generate thumbnail', error);
        if (!cancelled) {
          setState({ status: 'error' });
        }
      });

    return () => {
      cancelled = true;
      if (nextUrl) {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [file, maxWidth, maxHeight, mimeType, quality]);

  return { url, status };
}

function getThumbnailBlob(
  file: File,
  options: Required<UseThumbnailOptions>
): Promise<Blob> {
  let cached = thumbnailBlobCache.get(file);
  if (!cached) {
    cached = generateThumbnailBlob(file, options).catch((error) => {
      thumbnailBlobCache.delete(file);
      throw error;
    });
    thumbnailBlobCache.set(file, cached);
  }
  return cached;
}

async function generateThumbnailBlob(
  file: File,
  options: Required<UseThumbnailOptions>
): Promise<Blob> {
  const source = await loadImageSource(file);
  const { width, height } = constrainDimensions(
    source.width,
    source.height,
    options.maxWidth,
    options.maxHeight
  );

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  if (!context) {
    source.cleanup?.();
    throw new Error('Canvas 2D context is not available');
  }

  source.draw(context, width, height);
  source.cleanup?.();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to Blob'));
        }
      },
      options.mimeType,
      options.quality
    );
  });
}

type ImageSource = {
  width: number;
  height: number;
  draw: (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => void;
  cleanup?: () => void;
};

async function loadImageSource(file: File): Promise<ImageSource> {
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, width, height) => {
          ctx.drawImage(bitmap, 0, 0, width, height);
        },
        cleanup: () => {
          bitmap.close();
        },
      };
    } catch (error) {
      console.warn('createImageBitmap failed, falling back to <img>', error);
    }
  }

  const dataUrl = await readFileAsDataUrl(file);
  const imageElement = await loadHtmlImage(dataUrl);
  return {
    width: imageElement.naturalWidth,
    height: imageElement.naturalHeight,
    draw: (ctx, width, height) => {
      ctx.drawImage(imageElement, 0, 0, width, height);
    },
  };
}

function constrainDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
